"use client";

import useContentAutoResume from "@/hooks/useContentAutoResume";
import CustomButton from "@/components/common/button";
import Cards from "@/components/content/cards";
import Comments from "@/components/content/comments";
import PlayIcon from "@/helpers/svg/playIcon.svg";
import { Card, CardBody } from "@nextui-org/react";
import _ from "lodash";
import { useEffect, useMemo, useState, useCallback } from "react";
import { ROUTES } from "@/routes/appRoutes";
import { CinemaTicketApi } from "@/config/cms";
import ContentActions from "@/components/content/contentActions";
import UserReport from "@/components/content/userReport";
import { useRouter } from "next/navigation";
import axiosInstance from "@/config/axiosInstance";
import { getBillingApiRoute, getCMSApiRoute } from "@/helpers/api.util";
import { useSelector } from "react-redux";
import { mergeArtistDuplicates } from "@/helpers/data.utils";
import { getServerNow } from "@/services/publicAuthConfigService";
import styled from "styled-components";
import PreviewModal from "@/components/content/previewModal";
import ContentSuggestionsRail from "@/components/content/ContentSuggestionsRail";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import DetailHeroScene from "@/components/common/DetailHeroScene";
import { getPathname } from "@/helpers/browser.utils";
import { useContentViewAnalytics } from "@/hooks/useMediaAnalytics";
import {
  buildContentAnalyticsPayload,
  sendPlayIntent,
} from "@/services/mediaAnalyticsService";
import { isComingSoon } from "@/utils/contentStatus";

export const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: -2px;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: "";
    background-image: url(/detail-shadow.png);
    background-position: right;
    background-size: 50% 100%;
    background-repeat: no-repeat;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5;
  }

  &::after {
    content: "";
    background-image: url(/options-shadow.png);
    background-position: bottom;
    background-size: cover;
    background-repeat: no-repeat;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5;
  }
`;

const MovieDetail = ({ data, isAdminPreview = false }) => {
  const router = useRouter();
  const prepareAutoResume = useContentAutoResume(data?.slug);
  useContentViewAnalytics(data, { enabled: !isAdminPreview });
  const [isPurchased, setIsPurchased] = useState(false);
  const [cinemaTicketSlug, setCinemaTicketSlug] = useState("");
  const { isLogin } = useSelector((state) => state.Auth?.profile);
  const { canPlay, canPlayLoading } = useSubscriptionStatus();
  const comingSoon = isComingSoon(data?.status);

  const { artists } = data;

  const artistList = useMemo(
    () =>
      artists?.reduce((acc, i) => {
        const { farsiName, image, _id, slug } = i.artist?.meta?.bin || {};
        return acc.concat({
          artist: {
            farsiName,
            profession: i.profession?.name,
            contentPhotos: [{ type: "banner", src: image }],
            id: _id,
            slug,
            priority: i.priority ?? i.artist?.priority,
          },
        });
      }, []) || [],
    [artists]
  );

  const isCinemaTicketPurchased = useCallback(async (slug) => {
    try {
      const response = await axiosInstance.get(getBillingApiRoute("payment"), {
        params: {
          pageIndex: 1,
          pageNumber: 1,
          orderType: "cinema_ticket",
          status: 1,
          ticketKey: slug,
        },
      });

      if (response.status === 200 && response.data?.data?.results?.length > 0) {
        const { data } = response.data;
        data?.results?.forEach((item) => {
          if (
            item.orderType === "cinema_ticket" &&
            +item.expireAt > getServerNow()
          ) {
            setIsPurchased(true);
          }
        });
      }
    } catch (error) {
      console.log(`SSR Error on CinemaTicket Get: ${error}`);
      return {};
    }
  }, []);

  const getCinemaTicketData = useCallback(
    async (slug) => {
      try {
        const response =
          await CinemaTicketApi.prototype.cinemaTicketControllerGetTicket(
            1,
            1,
            decodeURI(slug),
            undefined
          );

        if (response.status === 200) {
          const { data } = response.data;
          setCinemaTicketSlug(data.results[0]?.slug);
          await isCinemaTicketPurchased(data.results[0]?.slug);
        }
      } catch (error) {
        console.log(`SSR Error on CinemaTicket Get: ${error}`);
        return {};
      }
    },
    [isCinemaTicketPurchased]
  );

  const reportContentWatch = useCallback(async () => {
    try {
      await axiosInstance.post(getCMSApiRoute("action/watch"), {
        contentKey: data.slug,
        isEpisode: 0,
      });
    } catch (error) {
      console.log(error);
    }
  }, [data.slug]);

  const getPlayUrl = useCallback(() => {
    return ROUTES.player + "/" + data.slug;
  }, [data.slug]);

  const reportPlayIntent = useCallback(() => {
    const payload = buildContentAnalyticsPayload(data, {
      surface: "content_detail",
      source: "primary_play_button",
    });
    if (payload) sendPlayIntent(payload);
  }, [data]);

  const handleWatchEvent = useCallback(
    async (movieData) => {
      if (isComingSoon(movieData?.status)) return;
      try {
        if (movieData.isTicketing == 1) {
          if (isPurchased === false) {
            if (!isLogin) {
              router.push(
                `/auth/login?ref=${encodeURIComponent(
                  ROUTES.buyTicket + `/${cinemaTicketSlug}`
                )}`
              );
            } else {
              router.push(
                ROUTES.buyTicket + `/${decodeURIComponent(cinemaTicketSlug)}`
              );
            }
          } else {
            if (!isLogin) {
              router.push(
                `/auth/login?ref=${encodeURIComponent(
                  `/player/${movieData.slug}?isTicketing=1&contentSlug=${cinemaTicketSlug}`
                )}`
              );
            } else {
              reportPlayIntent();
              prepareAutoResume();
              router.push(
                `/player/${encodeURIComponent(
                  movieData.slug
                )}?isTicketing=1&contentSlug=${cinemaTicketSlug}`
              );
            }
          }
        } else {
          if (movieData.isFree === 1) {
            if (!isLogin) {
              return router.push(
                `/auth/login?ref=${encodeURIComponent(getPlayUrl())}`
              );
            } else {
              const playUrl = getPlayUrl();
              await reportContentWatch();
              reportPlayIntent();
              prepareAutoResume();
              router.push(playUrl);
            }
          } else {
            if (!isLogin) {
              return router.push(
                `/auth/login?ref=${encodeURIComponent(getPathname())}`
              );
            } else {
              if (!canPlay) {
                return router.push("/subscription");
              } else {
                const playUrl = getPlayUrl();
                await reportContentWatch();
                reportPlayIntent();
                prepareAutoResume();
                router.push(playUrl);
              }
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [
      isPurchased,
      isLogin,
      cinemaTicketSlug,
      canPlay,
      getPlayUrl,
      reportContentWatch,
      reportPlayIntent,
      prepareAutoResume,
      router,
    ]
  );

  const getWatchLabel = useCallback(() => {
    if (!isLogin) {
      if (data.isTicketing != 1 || isPurchased === true) {
        return "ورود و تماشا";
      } else {
        return "ورود و خرید بلیط";
      }
    } else {
      if (data.isTicketing != 1 || isPurchased === true) {
        if (data.isFree === 1) {
          return "تماشا کن";
        } else if (data.isFree !== 1 && canPlay) {
          return "تماشا کن";
        } else {
          return "خرید اشتراک و تماشا";
        }
      } else {
        return "خرید بلیط";
      }
    }
  }, [isLogin, data.isTicketing, data.isFree, isPurchased, canPlay]);

  useEffect(() => {
    if (isLogin && !comingSoon) {
      getCinemaTicketData(data.slug);
    }
  }, [
    getCinemaTicketData,
    data.slug,
    isLogin,
    comingSoon,
  ]);

  if (!data) {
    return (
      <div className="min-h-[100vh] flex justify-center items-center">
        <Card className="dark p-10">
          <CardBody>محتوای مورد نظر یافت نشد</CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="content-detail-page h-auto min-h-screen">
      <DetailHeroScene
        content={{
          ...data,
          titleEn: data.titleEN,
          imdbRate: data.imdb_rate || data.imdbRate,
        }}
        renderButtons={() => (
          <>
            {comingSoon ? (
              <span className="inline-flex min-h-11 items-center rounded-full border border-white/30 bg-white/10 px-5 font-semibold text-white">
                فعلاً قابل تماشا نیست
              </span>
            ) : (
              <CustomButton
                label={getWatchLabel()}
                color="primary"
                textColor="text-primary-white"
                classNames="content-detail-play-button"
                rightIcon={<PlayIcon />}
                onClick={() => handleWatchEvent(data)}
                isLoading={canPlayLoading}
                showLabelOnLoading={false}
                size="lg"
              />
            )}
            <PreviewModal src={data.trailer} />
            <ContentActions contentKey={data.slug} />
            <UserReport contentKey={data.slug} data={data} />
          </>
        )}
      />
      {artistList?.length > 0 && (
        <section className="detail-artist-section lg:py-20 md:py-12 py-6 md:container mx-auto mb-5 relative z-10 px-5 2xl:px-0 xlg:max-w-[1920px]">
          <Cards
            items={mergeArtistDuplicates(artistList)}
            shape="square"
            isArtist={true}
            isSwiper={true}
            noTag={true}
            categoryName="هنرمندان"
            showTitle={true}
            type="artist"
            noContainer={true}
          />
        </section>
      )}
      <ContentSuggestionsRail
        slug={data.slug}
        type={data?.contentType?.kind || "movie"}
        enabled={!isAdminPreview}
      />
      <div className="py-10"></div>
      <section className="detail-comments-section mt-10 mx-auto w-full md:container xlg:max-w-[1920px]">
        <Comments contentKey={data.slug} />
      </section>
    </div>
  );
};

export default MovieDetail;
